// Pure decision helpers for the FirstWeekCard. Kept free of React so they can be
// unit-tested without an RTL harness (this repo has none) — mirrors the pattern
// used by nudge-banner-logic.ts.
//
// These shapes must stay structurally consistent with the backend's
// get-first-week-checklist response (see whatsapp-scheduler-backend
// src/services/first-week/checklist-steps.ts) — the source of truth for the
// wire contract lives there.

import type { ConnectionKind } from '@common-reducers/ConnectedEnginesSlice';

export type StepId = 'connect' | 'self_test' | 'workflow' | 'return';

export interface FirstWeekStep {
  id: StepId;
  done: boolean;
  doneAt: string | null;
}

export interface FirstWeekChecklist {
  steps: FirstWeekStep[];
  workflow: { messageId: string; fireAt: string } | null;
  ownWid: string | null;
}

const EXTENSION_APP_ORIGIN = 'https://app.blueticks.co';

/**
 * Card-render gate.
 * Renders ONLY when: the checklist has the full 4-step shape, dunning is not
 * showing (dunning always wins), the card hasn't been dismissed, and there is
 * still at least one step left to do.
 */
export function shouldRenderCard(
  payload: FirstWeekChecklist | null | undefined,
  dunningVisible: boolean,
  dismissed: boolean
): boolean {
  if (!payload) return false;
  if (dunningVisible) return false;
  if (dismissed) return false;
  if (payload.steps.length !== 4) return false;
  const allDone = payload.steps.every((s) => s.done);
  if (allDone) return false;
  return true;
}

/** First not-done step, in checklist order; null once everything is done. */
export function nextStep(steps: FirstWeekStep[]): StepId | null {
  const found = steps.find((s) => !s.done);
  return found ? found.id : null;
}

/**
 * Seed values for the "do it for me" CTA on a given step:
 * - self_test: schedule a message to the user's own WhatsApp id, due now.
 * - workflow: schedule the guided workflow message for 10:00 local the next day.
 * - connect / return: no seed — those steps are handled via stepHref/other UI.
 */
export function seedForStep(
  step: StepId,
  ownWid: string | null,
  now: Date
): { contactWid?: string; dueDate: Date; guided: boolean; dueDateSetByUser: boolean } | null {
  // BE#657 M2 — both CTAs seed a dueDate from a deliberate user click, so mark it
  // user-set; otherwise compose opens with Send disabled until the user re-confirms
  // the time in the picker (an onboarding-activation regression).
  if (step === 'self_test') {
    return { contactWid: ownWid ?? undefined, dueDate: now, guided: false, dueDateSetByUser: true };
  }
  if (step === 'workflow') {
    const dueDate = new Date(now);
    dueDate.setDate(dueDate.getDate() + 1);
    dueDate.setHours(10, 0, 0, 0);
    return { dueDate, guided: true, dueDateSetByUser: true };
  }
  return null;
}

/**
 * Resolve the CTA href for a step, per surface. 'connect' goes to the focused
 * /onboarding (connect → first message, one step at a time); the always-on
 * offer on 'return' goes to the connections page, where a connected user adds
 * an always-on connection. The other steps are driven by seedForStep instead.
 */
export function stepHref(step: StepId, surface: 'web' | 'extension'): string | null {
  const origin = surface === 'extension' ? EXTENSION_APP_ORIGIN : '';
  if (step === 'connect') return `${origin}/onboarding?from=${surface === 'extension' ? 'extension_row' : 'row'}`;
  if (step === 'return') return `${origin}/connections`;
  return null;
}

/**
 * Being connected is a live STATE, not only a past event: a user sitting in
 * WhatsApp Web (or with a live gateway) has step 1 done even if the server has
 * recorded a `whatsapp_connected` yet — asking
 * them to "Connect WhatsApp" from inside WhatsApp Web makes no sense.
 */
export function withLiveConnection(steps: FirstWeekStep[], kind: ConnectionKind): FirstWeekStep[] {
  if (kind === 'none') return steps;
  return steps.map((s) => (s.id === 'connect' && !s.done ? { ...s, done: true } : s));
}

/** Only once the user has seen it work (waiting for the real send), and only
 *  without a live gateway — a local connection sends only while WhatsApp Web is
 *  open — offer the always-on (remote) connection. */
export function shouldOfferAlwaysOn(current: StepId | null, kind: ConnectionKind): boolean {
  return current === 'return' && kind !== 'remote';
}
